import { isUploadSessionScheduleModalOpenAtom } from '@/features/uploadSessionSchedule/atoms';
import { useScheduleUpload } from '@/features/uploadSessionSchedule/hooks/useScheduleUpload';
import { useDocumentFilePaste } from '@heidi/design-system/file-upload';
import { useAtomValue } from 'jotai';

/**
 * Pasting a schedule screenshot/file anywhere over the sessions sidebar opens
 * the upload modal, using the same upload/preview flow as drag-and-drop and
 * browse.
 *
 * Paste is owned by the tab rather than the sidebar upload area's dropzone
 * because that area only reveals over an empty list — binding paste to it left
 * users who already have upcoming sessions unable to paste at all. The upload
 * modal owns paste while open, and `useDocumentFilePaste` skips editable
 * targets so a paste meant for the note editor is never swallowed.
 *
 * @param enabled caller-owned surface gate: the sessions sidebar is visible and
 * the user may create sessions.
 */
export const useSidebarSchedulePaste = ({ enabled }: { enabled: boolean }) => {
  const {
    acceptFormats,
    maxSize,
    isUploading,
    handleFilesAccepted,
    handleFilesRejected,
  } = useScheduleUpload({ entryPoint: 'sidebar' });
  const isUploadModalOpen = useAtomValue(isUploadSessionScheduleModalOpenAtom);

  useDocumentFilePaste({
    enabled: enabled && !isUploadModalOpen && !isUploading,
    accept: acceptFormats,
    maxFiles: 1,
    maxSize,
    onFilesAccepted: handleFilesAccepted,
    onFilesRejected: handleFilesRejected,
  });
};
